import React, { useContext, useEffect } from 'react';

import { MDText } from 'i18n-react';

import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';
import { INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { getOrdinal } from 'Utilities/formatter';
import { safeLocalStorage } from 'Utilities/storages';

import LocalTexts from '../../fastlane/homeVRM/homeVRMErrors/HomeVRMErrorsTexts.json';
import { DOCUMENT_KINDS, DOCUMENT_UPLOAD_TYPE } from '../DocumentCapture.helpers';
import { DocumentCaptureOfferContext } from '../DocumentCaptureContexts';

import { useGetFinanceData } from './FinanceProviderSection/useGetFinanceData';
import { ImageManagerContextProvider } from './hooks/useImageManager';
import type { StateSections } from './pages/uploadImageReducer.types';
import { getExitPath } from './UploadDocuments.helpers';
import type { UploadDocumentsPageProps } from './UploadDocumentsPage.types';
import { UploadDocumentsPageView } from './UploadDocumentsPageView';
import { UploadDocumentSections } from './UploadDocumentsSections';

import fslStyles from './FinanceProviderSection/FinanceProviderCompleteSection.scss';

export const exitPath = ({ type, vrm }: { type: DOCUMENT_UPLOAD_TYPE; vrm: string }) => {
  const exitPathname = getExitPath(type);

  return `/${vrm}/${exitPathname}`;
};

// Needed, in order to pass generic error into toast
const VehicleGenerationErrorsT = new MDText(LocalTexts.vehicle_generation_failure);

const UploadDocumentsPage = ({
  document,
  kind,
  reactivated = false,
  uploadDocType = DOCUMENT_UPLOAD_TYPE.DEFAULT,
}: UploadDocumentsPageProps) => {
  const { enquiryId, id: dpEnquiryId, valuation } = useContext(DocumentCaptureOfferContext) || {};

  const {
    loading: financeLoading,
  } = useGetFinanceData({ dpEnquiryId });

  const docIsFinanceLetter = kind === DOCUMENT_KINDS.FINANCE_SETTLEMENT_LETTER;
  const financePageEnabled = docIsFinanceLetter;

  useEffect(() => {
    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.UPLOAD_DOCUMENTS_PAGE_LOADED(kind);
  }, [kind]);

  useEffect(() => {
    // After page has rendered, check if enquiry ID is present similar to how we checked it in the hooks
    if (!valuation?.valuation_ref) {
      window?.Sentry?.captureException(new Error('enquiryId is undefined'));

      toast({
        content: VehicleGenerationErrorsT.translate('detail'),
        heading: VehicleGenerationErrorsT.translate('title'),
        variant: INFOBOX_VARIANT.ERROR,
      });
    }
  }, [valuation?.valuation_ref]);

  const hideStepperEnquiries = safeLocalStorage.getItem('hideStepper') || [];

  if (!hideStepperEnquiries.includes(enquiryId)) {
    hideStepperEnquiries.push(enquiryId);
    safeLocalStorage.setItem('hideStepper', hideStepperEnquiries);
  }

  const images = document?.images ?? [];
  const uploadSection = UploadDocumentSections[kind];

  const initialInputs = uploadSection.inputs
    .map((input, index) => {
      const image = images.find((i) => i.pageNumber === index + 1);
      return (
        {
          ...input,
          ...image,
        }
      );
    });

  // map previously saved extra pages onto input state
  if (uploadSection.addAnotherPage && document?.images && Array.isArray(document.images)) {
    document.images.forEach((image) => {
      if (!initialInputs.find((initialInput) => initialInput.documentId === image.documentId)) {
        const input = {
          kind: document?.kind,
          name: '',
          touched: false,
          ...image,
          label: `${getOrdinal(image.pageNumber)} page`,
        };
        initialInputs.push(
          input,
        );
      }
    });
  }

  const initialSections = {
    ...uploadSection,
    inputs: initialInputs,
  } as StateSections;

  // Stop page flashing between different components in FSL flow
  if (financeLoading && financePageEnabled) {
    return <div className={fslStyles.loadingContainer}>
      <LoadingIndicator size="large" />
    </div>;
  }

  return (

    <ImageManagerContextProvider
      initialSections={initialSections}
      kind={kind}
      reactivated={Boolean(reactivated)}
      uploadDocType={uploadDocType}
    >
      <UploadDocumentsPageView document={document} kind={kind} uploadDocType={uploadDocType} />
    </ImageManagerContextProvider>
  );
};

// eslint-disable-next-line import/no-default-export
export default UploadDocumentsPage;
